// useAdminAuth — Google sign-in flow (Day 12 rewrite).
//
// onAuthStateChanged listener decides status:
//   - no user                              → "needs-signin"
//   - user.email endsWith @lifesolutions   → "signed-in" with email
//   - user.email anything else             → "not-worker" (sign them out)

import { getFirebase } from "@goodbye/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useEffect, useState } from "react";

const WORKER_DOMAIN = "@lifesolutions.org.tw";

export type AdminAuthStatus =
  | "loading"
  | "signed-in"
  | "needs-signin"
  | "not-worker"
  | "error";

export interface AdminAuthState {
  status: AdminAuthStatus;
  email: string | null;
  uid: string | null;
  error: Error | null;
}

const INITIAL: AdminAuthState = {
  status: "loading",
  email: null,
  uid: null,
  error: null,
};

export function useAdminAuth(): AdminAuthState {
  const [state, setState] = useState<AdminAuthState>(INITIAL);

  useEffect(() => {
    const { auth } = getFirebase();
    const unsub = onAuthStateChanged(
      auth,
      async (user) => {
        if (!user) {
          setState({ status: "needs-signin", email: null, uid: null, error: null });
          return;
        }
        const email = user.email?.toLowerCase() ?? null;
        if (!email || !email.endsWith(WORKER_DOMAIN)) {
          await auth.signOut();
          setState({ status: "not-worker", email, uid: null, error: null });
          return;
        }
        setState({ status: "signed-in", email, uid: user.uid, error: null });
      },
      (err) => {
        setState({
          status: "error",
          email: null,
          uid: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      },
    );
    return () => unsub();
  }, []);

  return state;
}
